import { type DocumentTemplate } from '@alpod-ai-writer/shared';
import clsx from 'clsx';
import { CheckIcon, CircleDashedIcon } from 'lucide-react';

import { type DocumentStructure, } from '../hooks/useDocumentStructure';
import type { SectionAnchor } from '../hooks/useSectionNav.hook';
import type { EditorSection } from '../types/editorSection.types';

export interface DocumentOutlineProps {
    template: DocumentTemplate;
    specialSections?: EditorSection[];
    currentSection: SectionAnchor|null;
    structure: DocumentStructure;
    isUnlocked?: boolean;
    className?: string;
    onSelect: (anchor: SectionAnchor) => void;
}

export const DocumentOutline: React.FC<DocumentOutlineProps> = ({
    template,
    specialSections,
    structure,
    currentSection,
    isUnlocked,
    className,
    onSelect
}) => {
    const { sections } = structure;

    return (
        <nav className={clsx(
            'flex flex-col gap-1.5 list-none md:max-w-[16rem]',
            className
        )}>
            {specialSections?.map((section) => {
                const isCurrent = section.id === currentSection?.sectionId;
                const isDisabled = false;

                return (
                    <li key={section.id}>
                        <button
                            className={clsx(
                                'w-full flex flex-row items-center justify-start text-left gap-1 text-sm py-2 px-4 rounded-full transition-colors duration-100',
                                'sticky top-0',
                                isDisabled && 'text-gray-500',
                                !isDisabled && 'cursor-pointer',
                                !isDisabled && (isCurrent
                                    ? 'bg-blue-200 hover:bg-blue-300 font-semibold'
                                    : 'hover:bg-gray-200 font-medium'
                                ),
                            )}
                            onClick={() => !isDisabled && onSelect({ sectionId: section.id })}
                            aria-label={section.title}
                            disabled={isDisabled}
                        >
                            {/* <CheckIcon className='text-lime-700' size={16} /> */}
                            <span>{section.title}</span>
                        </button>
                    </li>
                );
            })}

            {(specialSections?.length ?? 0 > 0) && (
                <div className='h-[1px] bg-gray-200 mx-4 my-1 shrink-0' />
            )}

            {template.config.sections.map((section) => {
                // In case the structure is not yet computed (eg. when switching templates)
                if (!sections[section.id]) {
                    return null;
                }

                const isCurrent = section.id === currentSection?.sectionId;
                const chapters = sections[section.id].chapters || [];
                const isDisabled = !isUnlocked;
                const isCompleted = sections[section.id].completed;

                return (
                    <li key={section.id}>
                        <button
                            className={clsx(
                                'w-full flex flex-row items-center justify-start text-left gap-2 text-sm py-2 px-4 rounded-full transition-colors duration-100',
                                'sticky top-0',
                                isDisabled && 'text-gray-500',
                                !isDisabled && 'cursor-pointer',
                                !isDisabled && (isCurrent
                                    ? 'bg-blue-200 hover:bg-blue-300 font-semibold'
                                    : 'hover:bg-gray-200 font-medium'
                                )
                            )}
                            onClick={() => !isDisabled && onSelect({ sectionId: section.id })}
                            disabled={isDisabled}
                            aria-label={section.title}
                        >
                            {!isCompleted && (
                                <CircleDashedIcon className='text-gray-400' size={16} />
                            )}
                            {isCompleted && (
                                <CheckIcon className='text-lime-600' size={16} strokeWidth={3} />
                            )}
                            <span>{section.title}</span>
                        </button>
                        {isCurrent && chapters.length > 0 && (
                            <ul className='my-2 pl-4 list-none flex flex-col gap-0'>
                                {chapters.map((chapter) => (
                                    <button
                                        key={chapter.title}
                                        className={clsx(
                                            'pr-4 py-1.5 text-sm font-medium text-left text-gray-800 hover:text-primary-500 border-l-2 border-gray-300 hover:border-primary-500',
                                            chapter.level === 1 && 'pl-4',
                                            chapter.level === 2 && 'pl-8',
                                            chapter.level === 3 && 'pl-12',
                                            chapter.level === 4 && 'pl-16',
                                            chapter.level === 5 && 'pl-20'
                                        )}
                                        onClick={() => onSelect({ sectionId: section.id, headingId: chapter.id })}
                                    >{chapter.title}</button>
                                ))}
                            </ul>
                        )}
                    </li>
                );
            })}
        </nav>
    );
};
